/**
 * HI-SI Dashboard - Enhanced WebSocket Client
 * Improved connection handling for production environments
 */

import { io, Socket } from 'socket.io-client';
import type { WebSocketEvents } from '@/types/execution';
// Removed react-hot-toast - connection status shown in UI

// Toggle verbose logging via Vite env or runtime flag
const WS_DEBUG: boolean = (import.meta as any)?.env?.VITE_WS_DEBUG === 'true'
  || (typeof window !== 'undefined' && !!(window as any).__WS_DEBUG__);

class EnhancedWebSocketClient {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectInterval = 1000;
  private isConnected = false;
  private eventListeners = new Map<string, Set<Function>>();
  private connectionTimeout: NodeJS.Timeout | null = null;
  private pingInterval: NodeJS.Timeout | null = null;

  constructor(private url: string, private token?: string) {}

  connect(): Promise<void> {
    return new Promise((resolve) => {
      try {
        if (WS_DEBUG) console.log('🔌 Attempting WebSocket connection to:', this.url);
        
        // Parse URL to determine if we need secure transport
        const isSecure = this.url.startsWith('https://') || this.url.startsWith('wss://');
        
        // Enhanced connection options for production
        this.socket = io(this.url, {
          auth: {
            token: this.token
          },
          // Force WebSocket transport first, fallback to polling
          transports: isSecure ? ['polling', 'websocket'] : ['websocket', 'polling'],
          // Increase timeouts for slow Render.com cold starts
          timeout: 20000,
          // Connection options
          reconnection: true,
          reconnectionAttempts: 10,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          randomizationFactor: 0.5,
          // Additional options for production
          upgrade: true,
          rememberUpgrade: true,
          // Path configuration
          path: '/socket.io/',
          // Query parameters
          query: {
            client: 'dashboard',
            version: '1.0.0'
          }
        });

        // Set connection timeout
        this.connectionTimeout = setTimeout(() => {
          if (!this.isConnected) {
            console.warn('⏱️ Connection timeout, falling back to offline mode');
            this.socket?.disconnect();
            resolve(); // Resolve anyway to allow offline mode
          }
        }, 25000);

        this.socket.on('connect', () => {
          if (WS_DEBUG) {
            console.log('✅ WebSocket connected successfully');
            console.log('📡 Socket ID:', this.socket?.id);
            console.log('🚀 Transport:', this.socket?.io.engine.transport.name);
          }
          
          this.isConnected = true;
          this.reconnectAttempts = 0;
          
          // Clear connection timeout
          if (this.connectionTimeout) {
            clearTimeout(this.connectionTimeout);
            this.connectionTimeout = null;
          }
          
          // Start ping interval to keep connection alive
          this.startPingInterval();
          
          // Emit local connection established event for subscribers
          this.emit('connection:established', {
            user_id: 'dashboard',
            timestamp: new Date().toISOString()
          } as any);

          // Notify success
          
          resolve();
        });

        this.socket.on('disconnect', (reason) => {
          if (WS_DEBUG) console.log('🔌 WebSocket disconnected:', reason);
          this.isConnected = false;
          
          // Stop ping interval
          this.stopPingInterval();
          
          this.emit('connection:lost', {
            reason,
            timestamp: new Date().toISOString()
          });

          // Handle different disconnect reasons
          switch(reason) {
            case 'io server disconnect':
              // Server initiated disconnect
              if (WS_DEBUG) console.log('Server requested disconnect');
              break;
            case 'ping timeout':
              // Ping timeout - try to reconnect
              if (WS_DEBUG) console.log('Ping timeout, attempting reconnect...');
              this.handleReconnect();
              break;
            case 'transport close':
              // Transport failure - try to reconnect
              if (WS_DEBUG) console.log('Transport closed, attempting reconnect...');
              this.handleReconnect();
              break;
            default:
              // Other reasons - attempt reconnect
              this.handleReconnect();
          }
        });

        this.socket.on('connect_error', (error: any) => {
          if (WS_DEBUG) {
            console.error('🔌 WebSocket connection error:', error.message);
            console.error('Error type:', error.type);
          }
          this.isConnected = false;
          
          // More detailed error logging
          if (error.message.includes('CORS')) {
            console.error('CORS issue detected. Check backend allowed origins.');
          } else if (error.message.includes('xhr poll error')) {
            console.error('Polling error. Server may be down or unreachable.');
          }
          
          if (this.reconnectAttempts === 0) {
            // First connection attempt failed - resolve anyway for offline mode
            if (this.connectionTimeout) {
              clearTimeout(this.connectionTimeout);
              this.connectionTimeout = null;
            }
            if (WS_DEBUG) console.log('⚠️ Initial connection failed, running in offline mode');
            resolve(); // Allow app to run in offline mode
          } else {
            // Reconnection attempt failed
            this.handleReconnect();
          }
        });

        // Handle reconnection events
        this.socket.io.on('reconnect', (attempt) => {
          if (WS_DEBUG) {
            console.log(`✅ Reconnected after ${attempt} attempts`);
            console.log("Reconnected to backend");
          }
        });

        this.socket.io.on('reconnect_attempt', (attempt) => {
          if (WS_DEBUG) console.log(`🔄 Reconnection attempt ${attempt}`);
        });

        this.socket.io.on('reconnect_error', (error) => {
          if (WS_DEBUG) console.error('❌ Reconnection error:', error.message);
        });

        this.socket.io.on('reconnect_failed', () => {
          if (WS_DEBUG) {
            console.error('❌ Failed to reconnect after maximum attempts');
            console.error("Unable to reconnect to backend");
          }
        });

        // Set up event forwarding
        this.setupEventForwarding();

      } catch (error) {
        console.error('🔌 Failed to initialize WebSocket:', error);
        // Resolve anyway to allow offline mode
        resolve();
      }
    });
  }

  private startPingInterval() {
    // Send ping every 25 seconds to keep connection alive
    this.pingInterval = setInterval(() => {
      if (this.socket?.connected) {
        this.socket.emit('ping');
      }
    }, 25000);
  }

  private stopPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private setupEventForwarding() {
    if (!this.socket) return;

    // Forward all backend events to local listeners (logging only when WS_DEBUG)
    const eventTypes: (keyof WebSocketEvents)[] = [
      'connection:established',
      'connection:lost',
      'execution:started',
      'execution:progress', 
      'execution:stage_completed',
      'execution:completed',
      'execution:failed',
      'execution:paused',
      'execution:resumed',
      'execution:cancelled',
      'execution:status',
      'execution:progress_history',
      'audio:available',
      'cost:update',
      'cost:budget_warning',
      'cost:budget_exceeded',
      'cost:history',
      'quality:score_update',
      'quality:threshold_missed',
      'quality:improvement_detected',
      'system:maintenance',
      'system:capacity_warning',
      'system:error',
      'log:entry',
      'log:batch',
      'pipeline:content_ready',  // Add content ready event
      'pipeline:scores_ready',    // Add scores ready event
      'error'
    ];

    eventTypes.forEach((eventType) => {
      this.socket?.on(eventType, (data: any) => {
        if (WS_DEBUG) console.log(`📨 Received event: ${eventType}`, data);
        this.emit(eventType, data);
      });
    });

    // Handle pong response
    this.socket.on('pong', () => {
      console.log('🏓 Pong received');
    });
  }

  private handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      console.error("Connection lost. Please refresh the page.");
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectInterval * Math.pow(2, this.reconnectAttempts - 1),
      30000
    );

    if (WS_DEBUG) console.log(`Attempting reconnection ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);
    
    setTimeout(() => {
      if (!this.isConnected && this.socket) {
        this.socket.connect();
      }
    }, delay);
  }

  disconnect() {
    this.stopPingInterval();
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
    
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.isConnected = false;
    this.eventListeners.clear();
  }

  on<K extends keyof WebSocketEvents>(
    event: K,
    callback: (data: WebSocketEvents[K]) => void
  ): () => void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event)?.add(callback);
    
    // Return unsubscribe function
    return () => {
      this.eventListeners.get(event)?.delete(callback);
    };
  }

  off<K extends keyof WebSocketEvents>(
    event: K,
    callback: (data: WebSocketEvents[K]) => void
  ) {
    this.eventListeners.get(event)?.delete(callback);
  }

  emit<K extends keyof WebSocketEvents>(
    event: K,
    data: WebSocketEvents[K]
  ) {
    this.eventListeners.get(event)?.forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in event listener for ${event}:`, error);
      }
    });
  }

  sendMessage(event: string, data: any) {
    if (!this.socket?.connected) {
      console.warn('Cannot send message: socket not connected');
      console.error("Not connected to backend");
      return;
    }
    this.socket.emit(event, data);
  }

  // Execution management methods
  startExecution(config: any) {
    // Debug log what we're sending
    console.log('[WebSocketClient] startExecution sending:', {
      eventName: 'client:start_execution',
      configKeys: Object.keys(config),
      hasStageConfigs: {
        script: !!config.script,
        scriptMaxIterations: config.script?.max_iterations,
        vision: !!config.vision,
        visionMaxIterations: config.vision?.max_iterations
      },
      configSample: {
        scene_id: config.scene_id,
        asset_path: config.asset_path,
        budget_limit_usd: config.budget_limit_usd
      }
    });
    // Align with backend's client-initiated start convention
    this.sendMessage('client:start_execution', { config });
  }

  pauseExecution(executionId: string) {
    this.sendMessage('client:pause_execution', { execution_id: executionId });
  }

  resumeExecution(executionId: string) {
    this.sendMessage('client:resume_execution', { execution_id: executionId });
  }

  stopExecution(executionId: string) {
    this.sendMessage('client:stop_execution', { execution_id: executionId });
  }

  deleteExecution(executionId: string) {
    this.sendMessage('client:delete_execution', { execution_id: executionId });
  }

  duplicateExecution(executionId: string) {
    this.sendMessage('client:duplicate_execution', { execution_id: executionId });
  }

  subscribeToExecution(executionId: string, profileId?: string) {
    this.sendMessage('client:subscribe', { 
      execution_id: executionId,
      profile_id: profileId
    });
  }

  unsubscribeFromExecution(executionId: string, profileId?: string) {
    this.sendMessage('client:unsubscribe', { 
      execution_id: executionId,
      profile_id: profileId
    });
  }

  getConnectionStatus() {
    return this.isConnected;
  }

  getSocket() {
    return this.socket;
  }

  getServerUrl() {
    return this.url;
  }
}

// Singleton instance
let webSocketClient: EnhancedWebSocketClient | null = null;

export function createWebSocketClient(url: string, token?: string): EnhancedWebSocketClient {
  // Destroy existing client if URL changes
  if (webSocketClient && webSocketClient['url'] !== url) {
    webSocketClient.disconnect();
    webSocketClient = null;
  }
  
  // Create new client only if none exists
  if (!webSocketClient) {
    webSocketClient = new EnhancedWebSocketClient(url, token);
  }
  return webSocketClient;
}

export function getWebSocketClient(): EnhancedWebSocketClient | null {
  return webSocketClient;
}

export function destroyWebSocketClient() {
  if (webSocketClient) {
    webSocketClient.disconnect();
    webSocketClient = null;
  }
}

export default EnhancedWebSocketClient;